/**
 * The selectable date range for the booking calendar.
 *
 * Split out of `BookingTimeStep` so it can be tested without rendering the
 * step — and because it was wrong in a way no render test would have caught
 * on a UTC CI runner.
 *
 * @module booking/steps/clubCalendarBounds
 */

import {
  asUtcDate,
  clubInstantToPickerDate,
  type Timezone,
} from '@butter/library/datetime';
import { addDays } from 'date-fns';

/**
 * The club's current calendar day, as the browser-local `Date` a DatePicker
 * compares its own values against.
 *
 * Previously this read a `ZonedDate` with `getUTCFullYear/Month/Date`, on the
 * belief that a `ZonedDate` "stores club-local values in UTC fields". It does
 * not — a `ZonedDate` is shifted so its *local* accessors read club time, which
 * is why `branded.ts` documents it as safe for `.getHours()`/`.getMinutes()`
 * only. Reading the UTC fields instead added the browser's offset on top, so
 * the date rolled forward once `club hour + |browser offset| >= 24`: a New York
 * member lost same-day booking from 20:00, a Pacific member at an Eastern club
 * from 17:00. Under UTC — which CI runs — the offset is zero and it looked fine.
 */
export function clubTodayAsPickerDate(timezone: Timezone | undefined): Date {
  if (!timezone) {
    // Still a local MIDNIGHT, not the current instant. The midnight invariant is
    // the whole contract here, and `DatePicker` truncating to Y/M/D is a
    // coincidence of today's caller, not something this function may rely on.
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  return clubInstantToPickerDate(asUtcDate(new Date()), timezone);
}

/**
 * The last day a member may book, given their resolved advance-booking window.
 */
export function clubMaxBookableDate(
  timezone: Timezone | undefined,
  advanceBookingDays: number,
): Date {
  return addDays(clubTodayAsPickerDate(timezone), advanceBookingDays);
}
